import React, { useEffect, useMemo, useState } from "react";
import { Plus, Minus, WifiOff, UserPlus, X, Search, Megaphone, Check, Receipt, Banknote, Smartphone } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNetwork } from "@/context/NetworkContext";
import Layout from "@/components/Layout";
import { Card, Button, Chip, Skeleton, EmptyState, Toast, Modal, Field, Input } from "@/ui";
import { Package } from "lucide-react";

function peso(centavos) {
  const whole = Math.floor(Math.abs(centavos) / 100);
  const frac = String(Math.abs(centavos) % 100).padStart(2, "0");
  return `\u20b1${whole.toLocaleString()}.${frac}`;
}

export default function Counter() {
  const { shopId } = useAuth();
  const { online, enqueueOrder, flush } = useNetwork();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [cart, setCart] = useState({});
  const [pickFor, setPickFor] = useState(null);
  const [toast, setToast] = useState("");
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState("cash"); // cash | ewallet

  // customer
  const [customer, setCustomer] = useState(null);
  const [phoneLookup, setPhoneLookup] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupMsg, setLookupMsg] = useState("");
  const [showCust, setShowCust] = useState(false);
  const [custName, setCustName] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [custBusy, setCustBusy] = useState(false);
  const [custError, setCustError] = useState("");

  // upsell
  const [upsell, setUpsell] = useState(null);
  const [offered, setOffered] = useState(false);
  const [took, setTook] = useState(false);

  // order slip
  const [slip, setSlip] = useState(null);

  useEffect(() => {
    if (!shopId) return;
    api.get(`/products?shopId=${shopId}&forSale=1`).then((r) => setProducts(r.data.products)).catch((e) => setError(friendlyError(e))).finally(() => setLoading(false));
  }, [shopId]);

  const addLine = (p, variant) => {
    const variantName = variant ? variant.name : "";
    const unit = variant ? variant.priceCentavos : p.priceCentavos;
    const key = `${p.id}::${variantName}`;
    setCart((c) => ({ ...c, [key]: { productId: p.id, variantName, name: p.name + (variantName ? ` (${variantName})` : ""), unit, qty: (c[key]?.qty || 0) + 1 } }));
    setPickFor(null);
  };
  const onAdd = (p) => {
    if (p.variants && p.variants.length > 0) { setPickFor(pickFor === p.id ? null : p.id); return; }
    addLine(p, null);
  };
  const setQty = (key, d) => setCart((c) => {
    const line = c[key]; if (!line) return c;
    const qty = line.qty + d;
    const next = { ...c };
    if (qty <= 0) delete next[key]; else next[key] = { ...line, qty };
    return next;
  });

  const lines = Object.entries(cart);
  const total = useMemo(() => lines.reduce((s, [, l]) => s + l.unit * l.qty, 0), [lines]);
  const cartKey = lines.map(([k, l]) => `${k}x${l.qty}`).join("|");

  // The line to say: ask the backend for a suggested upsell whenever the cart changes.
  useEffect(() => {
    if (!shopId || lines.length === 0 || !online) { setUpsell(null); setOffered(false); setTook(false); return; }
    const body = { shopId, lines: lines.map(([, l]) => ({ productId: l.productId, variantName: l.variantName })) };
    api.post("/counter/upsell-suggest", body)
      .then((r) => { setUpsell(r.data.suggestion || null); setOffered(false); setTook(false); })
      .catch(() => setUpsell(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartKey, shopId, online]);

  const filtered = products.filter((p) => p.name.toLowerCase().includes(query.trim().toLowerCase()));

  const findCustomer = async () => {
    if (!phoneLookup.trim()) return;
    setLookupBusy(true); setLookupMsg("");
    try {
      const q = new URLSearchParams({ shopId: shopId || "", phone: phoneLookup.trim() }).toString();
      const r = await api.get(`/counter/find-customer?${q}`);
      if (r.data.found) { setCustomer(r.data.customer); setLookupMsg(""); setPhoneLookup(""); }
      else { setLookupMsg("No customer with that number. You can add them or take the order without one."); }
    } catch (e) { setLookupMsg(friendlyError(e)); }
    finally { setLookupBusy(false); }
  };

  const saveCustomer = async () => {
    setCustBusy(true); setCustError("");
    try {
      const r = await api.post("/customers", { shopId, name: custName.trim(), phone: custPhone.trim() });
      const c = r.data.customer;
      setCustomer({ id: c.id, name: c.name });
      setShowCust(false); setCustName(""); setCustPhone(""); setLookupMsg("");
    } catch (e) { setCustError(friendlyError(e)); }
    finally { setCustBusy(false); }
  };

  const recordSale = async () => {
    if (!lines.length) return;
    setBusy(true); setError("");
    const clientRef = `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const slipLines = lines.map(([, l]) => ({ name: l.name, qty: l.qty, unit: l.unit }));
    const payload = {
      shopId, channel: "counter", customerId: customer?.id || null, clientRef,
      paymentMethod: pay,
      lines: lines.map(([, l]) => ({ productId: l.productId, variantName: l.variantName, qty: l.qty })),
    };
    try {
      if (online) {
        const r = await api.post("/orders", payload);
        const o = r.data.order || {};
        // Record whether the upsell was offered / taken, attributed to this staff.
        if (upsell && (offered || took)) {
          try {
            await api.post("/counter/upsell-touch", {
              shopId, productId: upsell.productId, offered: offered || took, took,
              variantFrom: upsell.variantFrom, variantTo: upsell.variantTo, orderId: o.id,
            });
          } catch { /* attribution is best-effort */ }
        }
        setSlip({ orderId: o.id, lines: slipLines, total, pay, customerName: customer?.name,
                  dealSummary: o.dealSummary, net: o.net, discount: o.discountCentavos > 0 ? o.discount : null });
      } else {
        enqueueOrder(payload);
        setToast("saved \u2014 will send when you're back online");
        setTimeout(() => setToast(""), 3500);
      }
      setCart({}); setCustomer(null); setUpsell(null); setOffered(false); setTook(false);
    } catch (e) {
      enqueueOrder(payload);
      setToast("saved to send later \u2014 we couldn't reach the shop just now");
      setCart({}); flush();
      setTimeout(() => setToast(""), 3500);
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="row-between" style={{ marginBottom: 16 }}>
        <h1 className="t-h1">counter</h1>
        {!online ? <Chip tone="warn"><WifiOff size={16} strokeWidth={1.75} />working offline</Chip> : null}
      </div>
      {error ? <div className="err-text" data-testid="counter-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      <div className="counter-grid">
        <div>
          <div style={{ position: "relative", marginBottom: 12 }}>
            <Search size={18} style={{ position: "absolute", left: 12, top: 13, color: "var(--text-3)" }} />
            <input data-testid="counter-search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={"Search items\u2026"} aria-label="search items"
              style={{ width: "100%", padding: "12px 12px 12px 40px", borderRadius: 12, border: "1px solid var(--border-strong)", fontSize: 16 }} />
          </div>
          {loading ? (
            <div className="grid grid-2"><Skeleton variant="card" /><Skeleton variant="card" /></div>
          ) : filtered.length === 0 ? (
            <EmptyState icon={<Package size={20} strokeWidth={1.75} />} title={query ? "no items match your search" : "no items yet \u2014 add items first, then you can sell them here"} />
          ) : (
            <div className="grid grid-2" data-testid="counter-products">
              {filtered.map((p) => (
                <div key={p.id}>
                  <button className="counter-item" data-testid={`counter-add-${p.id}`} disabled={p.soldOut} onClick={() => onAdd(p)}>
                    <span className="t-h3">{p.name}</span>
                    <span className="row-between"><span className="num">{p.price}</span>{p.soldOut ? <Chip tone="bad" dot>sold out</Chip> : <span className="muted-3 t-small num">{p.available} left</span>}</span>
                  </button>
                  {pickFor === p.id ? (
                    <div className="row" style={{ flexWrap: "wrap", marginTop: 8 }}>
                      {p.variants.map((v) => (
                        <Button key={v.name} size="sm" disabled={v.soldOut} data-testid={`variant-${p.id}-${v.name}`} onClick={() => addLine(p, v)}>{`${v.name} \u00b7 ${peso(v.priceCentavos)}`}</Button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        <Card>
          <h2 className="t-h2" style={{ marginBottom: 12 }}>this sale</h2>

          {/* Customer: find by phone, or take the order without one. */}
          <div data-testid="customer-row" style={{ marginBottom: 12 }}>
            {customer ? (
              <div className="row-between">
                <Chip tone="good" dot>{customer.name}
                  <button className="menu-btn" aria-label="remove customer" data-testid="clear-customer"
                    style={{ marginLeft: 6, width: 20, height: 20 }} onClick={() => setCustomer(null)}><X size={13} strokeWidth={2} /></button>
                </Chip>
              </div>
            ) : (
              <div className="stack" style={{ gap: 8 }}>
                <div className="row" style={{ gap: 8 }}>
                  <Input data-testid="cust-phone-input" value={phoneLookup} inputMode="tel"
                    onChange={(e) => setPhoneLookup(e.target.value)} placeholder="Find customer by phone" />
                  <Button size="sm" data-testid="cust-find-btn" loading={lookupBusy} disabled={!online || !phoneLookup.trim()} onClick={findCustomer}>Find</Button>
                </div>
                {lookupMsg ? <p className="muted-3 t-small" data-testid="cust-lookup-msg">{lookupMsg}</p> : null}
                <Button size="sm" variant="ghost" data-testid="add-customer-btn" disabled={!online}
                  onClick={() => { setCustError(""); setShowCust(true); }}><UserPlus size={15} strokeWidth={1.75} />Add new customer</Button>
              </div>
            )}
          </div>
          <div className="divider" style={{ marginBottom: 12 }} />

          {lines.length === 0 ? (
            <p className="muted">nothing added yet. tap items on the left to build the sale.</p>
          ) : (
            <div data-testid="cart">
              {lines.map(([key, l]) => (
                <div key={key} className="cart-line">
                  <div><div className="t-body">{l.name}</div><div className="muted-3 t-small num">{peso(l.unit)} each</div></div>
                  <div className="qty">
                    <button className="btn" aria-label="less" onClick={() => setQty(key, -1)}><Minus size={16} strokeWidth={2} /></button>
                    <span className="num" style={{ minWidth: 20, textAlign: "center" }}>{l.qty}</span>
                    <button className="btn" aria-label="more" onClick={() => setQty(key, 1)}><Plus size={16} strokeWidth={2} /></button>
                  </div>
                </div>
              ))}
              <div className="row-between" style={{ marginTop: 12 }}>
                <span className="t-h3">total</span>
                <span className="num" data-testid="cart-total" style={{ fontSize: 40, fontWeight: 800, lineHeight: 1 }}>{peso(total)}</span>
              </div>
            </div>
          )}

          {/* The line to say */}
          {upsell ? (
            <div data-testid="upsell-banner" style={{ marginTop: 14, padding: 12, borderRadius: 12, background: "#fff7ed", border: "1px solid #fed7aa" }}>
              <div className="row" style={{ gap: 8, alignItems: "flex-start" }}>
                <Megaphone size={18} className="text-amber-600" style={{ marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div className="t-small muted" style={{ marginBottom: 2 }}>Say this:</div>
                  <div className="t-body" data-testid="upsell-line" style={{ fontWeight: 600 }}>{upsell.line}</div>
                  <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                    <Button size="sm" variant={offered || took ? "primary" : "secondary"} data-testid="upsell-offered"
                      onClick={() => { const n = !(offered || took); setOffered(n); if (!n) setTook(false); }}>
                      <Check size={14} /> {offered || took ? "Offered" : "I offered it"}
                    </Button>
                    <Button size="sm" variant={took ? "primary" : "ghost"} data-testid="upsell-took"
                      onClick={() => { setTook((v) => !v); setOffered(true); }}>
                      {took ? "They took it \u2713" : "They took it"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {/* Payment method */}
          {lines.length > 0 ? (
            <div className="row" style={{ gap: 8, marginTop: 14 }}>
              <Button className="btn-block" data-testid="pay-cash" variant={pay === "cash" ? "primary" : "secondary"} onClick={() => setPay("cash")}>
                <Banknote size={16} /> Cash
              </Button>
              <Button className="btn-block" data-testid="pay-ewallet" variant={pay === "ewallet" ? "primary" : "secondary"} onClick={() => setPay("ewallet")}>
                <Smartphone size={16} /> E-wallet
              </Button>
            </div>
          ) : null}

          <Button className="btn-block" variant="primary" style={{ marginTop: 14 }} data-testid="save-order-slip"
            disabled={lines.length === 0} loading={busy} onClick={recordSale}>
            <Receipt size={16} /> Save order slip
          </Button>
          <p className="muted-3 t-small" style={{ marginTop: 8, textAlign: "center" }} data-testid="not-receipt-note">
            This is an order record, not an official receipt (not a BIR receipt).
          </p>
        </Card>
      </div>

      {toast ? <div className="toast-wrap"><Toast tone="good" data-testid="counter-toast">{toast}</Toast></div> : null}

      {/* Order slip */}
      <Modal open={!!slip} title="Order slip" onClose={() => setSlip(null)}
        footer={<Button variant="primary" data-testid="slip-done" onClick={() => setSlip(null)}>Done</Button>}>
        {slip ? (
          <div className="stack" style={{ gap: 8 }} data-testid="order-slip">
            <div className="muted-3 t-small">Order #{String(slip.orderId || "").slice(0, 8)}</div>
            {slip.customerName ? <div className="t-small">For: {slip.customerName}</div> : null}
            <div className="divider" />
            {slip.lines.map((l, i) => (
              <div key={i} className="row-between t-body"><span>{l.qty} {"\u00d7"} {l.name}</span><span className="num">{peso(l.unit * l.qty)}</span></div>
            ))}
            {slip.discount ? <div className="row-between t-small" style={{ color: "var(--good)" }}><span>{slip.dealSummary || "discount"}</span><span>-{slip.discount}</span></div> : null}
            <div className="divider" />
            <div className="row-between t-h3"><span>Total</span><span className="num" data-testid="slip-total">{slip.net || peso(slip.total)}</span></div>
            <div className="row-between t-small muted"><span>Paid by</span><span>{slip.pay === "ewallet" ? "E-wallet" : "Cash"}</span></div>
            <div style={{ marginTop: 8, padding: 8, borderRadius: 8, background: "#f1f5f9" }} className="muted-3 t-small" data-testid="order-slip-note">
              This is an order record, not an official receipt (not a BIR receipt).
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal open={showCust} title="Add a new customer" onClose={() => !custBusy && setShowCust(false)}
        footer={<>
          <Button data-testid="cust-cancel" onClick={() => setShowCust(false)} disabled={custBusy}>go back</Button>
          <Button variant="primary" data-testid="cust-save" loading={custBusy}
            disabled={!custName.trim() || !custPhone.trim()} onClick={saveCustomer}>add customer</Button>
        </>}>
        <p className="muted t-small" style={{ marginBottom: 12 }}>this adds the person to BloomConnect first, then links them to this sale.</p>
        <div className="stack" style={{ gap: 12 }}>
          <Field label="name" htmlFor="cust-name">
            <Input id="cust-name" data-testid="cust-name" value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="e.g. Aling Rosa" />
          </Field>
          <Field label="mobile number" htmlFor="cust-phone" error={custError || undefined}>
            <Input id="cust-phone" data-testid="cust-phone" value={custPhone} inputMode="tel" onChange={(e) => setCustPhone(e.target.value)} placeholder="09XX XXX XXXX" />
          </Field>
        </div>
      </Modal>
    </Layout>
  );
}
